/**
 * SyncService
 * Handles synchronization of offline attendance records using check-in-out endpoint
 *
 * STRATEGY:
 * - Uses /api/warden/check-in-out endpoint (NOT /api/warden/sync)
 * - Processes records sequentially
 * - For offline check-in+check-out: Makes TWO sequential calls (check-in first, then check-out)
 */

import axiosInstance from './axios'
import type { LocalAttendanceRecord, CheckRequest, CheckResponse } from '@/types/attendance'
import { saveRecord } from '@/services/storage/local-attendance-storage'
import { logger } from '@/services/logger'

/**
 * Sync API Response (for compatibility with sync store)
 */
export interface SyncResponse {
    synced: number
    failed: number
    errors: Array<{
        recordId: string
        error: string
    }>
}

/**
 * Sync a single check-in record
 * @param record - Local attendance record
 * @returns Server recordId
 */
async function syncCheckIn(record: LocalAttendanceRecord): Promise<number> {
    logger.info(`[SyncService] Syncing check-in`, {
        localId: record.id,
        checkInTime: record.checkInTime
    })

    const request: CheckRequest = {
        action: 'check-in',
        workerId: record.workerId,
        timestamp: record.checkInTime,
        location: record.location,
        qrCode: record.qrCode
    }

    // Log request as string for logcat
    console.log('[SyncService] CHECK-IN REQUEST:')
    console.log(JSON.stringify(request, null, 2))
    console.log(`[SyncService] Request URL: /api/warden/check-in-out`)
    console.log(`[SyncService] Local record ID: ${record.id}`)

    const response = await axiosInstance.post<CheckResponse>('/api/warden/check-in-out', request)

    // Log response as string for logcat
    console.log('[SyncService] CHECK-IN RESPONSE:')
    console.log(JSON.stringify(response.data, null, 2))
    console.log(`[SyncService] Response status: ${response.status}`)
    console.log(`[SyncService] Server recordId: ${response.data.recordId}`)

    logger.info(`[SyncService] Check-in synced`, {
        localId: record.id,
        serverId: response.data.recordId
    })

    // Update local record with recordId and mark as synced
    record.recordId = response.data.recordId
    record.syncStatus = 'synced'
    record.updatedAt = response.data.serverTime
    await saveRecord(record)

    console.log(`[SyncService] Record updated: syncStatus='synced', recordId=${response.data.recordId}`)

    return response.data.recordId
}

/**
 * Sync a check-out for an existing record
 * @param record - Local attendance record (must have recordId)
 * @throws Error if recordId not found on server (404) - caller should handle and fetch history
 */
async function syncCheckOut(record: LocalAttendanceRecord): Promise<void> {
    if (!record.recordId) {
        throw new Error(`Cannot sync check-out without recordId for record ${record.id}`)
    }

    if (!record.checkOutTime) {
        throw new Error(`Cannot sync check-out without checkOutTime for record ${record.id}`)
    }

    logger.info(`[SyncService] Syncing check-out`, {
        localId: record.id,
        serverId: record.recordId,
        checkOutTime: record.checkOutTime
    })

    const request: CheckRequest = {
        action: 'check-out',
        workerId: record.workerId,
        timestamp: record.checkOutTime,
        location: record.location,
        qrCode: record.qrCode,
        recordId: record.recordId
    }

    // Log request as string for logcat
    console.log('[SyncService] CHECK-OUT REQUEST:')
    console.log(JSON.stringify(request, null, 2))
    console.log(`[SyncService] Request URL: /api/warden/check-in-out`)
    console.log(`[SyncService] Local record ID: ${record.id}`)

    try {
        const response = await axiosInstance.post<CheckResponse>('/api/warden/check-in-out', request)

        // Log response as string for logcat
        console.log('[SyncService] CHECK-OUT RESPONSE:')
        console.log(JSON.stringify(response.data, null, 2))
        console.log(`[SyncService] Response status: ${response.status}`)
        console.log(`[SyncService] Server recordId: ${response.data.recordId}`)

        logger.info(`[SyncService] Check-out synced`, {
            localId: record.id,
            serverId: response.data.recordId
        })

        // Mark as synced
        record.syncStatus = 'synced'
        record.updatedAt = response.data.serverTime
        await saveRecord(record)
    } catch (error) {
        // Log error as string for logcat
        console.log('[SyncService] CHECK-OUT ERROR:')
        console.log(`Error message: ${error instanceof Error ? error.message : 'Unknown error'}`)
        if (error instanceof Error && error.stack) {
            console.log(`Error stack: ${error.stack}`)
        }

        // Check if this is a 404 error (recordId not found)
        const is404 = error instanceof Error &&
            (error.message.includes('404') ||
             error.message.includes('not found') ||
             error.message.includes('Not Found'))

        if (is404) {
            logger.error(
                `[SyncService] RecordId ${record.recordId} not found on server (404) - may have been deleted/modified`,
                error as Error,
                {
                    localId: record.id,
                    serverId: record.recordId
                }
            )
            // Re-throw with clear message for caller to handle
            throw new Error(`RecordId ${record.recordId} not found on server - may have been deleted or modified`)
        }

        // Other errors - re-throw as is
        throw error
    }
}

/**
 * Sync pending records to the server using check-in-out endpoint
 *
 * SEQUENTIAL SYNC LOGIC:
 * 1. Check-in only (no checkout) → Single check-in call
 * 2. Check-out with recordId → Single check-out call
 * 3. Check-out without recordId (both offline) → TWO calls: check-in first, then check-out
 *
 * @param records - Array of local attendance records to sync
 * @returns Promise with sync results
 */
export async function syncPendingRecords(
    records: LocalAttendanceRecord[]
): Promise<SyncResponse> {
    console.log('='.repeat(80))
    console.log('[SyncService] ===== STARTING SYNC PROCESS =====')
    console.log(`[SyncService] Total pending records: ${records.length}`)
    console.log('[SyncService] Records to sync:')
    records.forEach((rec, idx) => {
        console.log(`  ${idx + 1}. ID: ${rec.id}, WorkerId: ${rec.workerId}, Action: ${rec.action}, SyncStatus: ${rec.syncStatus}`)
        console.log(`     CheckIn: ${rec.checkInTime}, CheckOut: ${rec.checkOutTime || 'N/A'}`)
        console.log(`     RecordId: ${rec.recordId || 'N/A'}, Location: ${JSON.stringify(rec.location)}`)
    })
    console.log('='.repeat(80))

    logger.info(`[SyncService] Syncing ${records.length} records using check-in-out endpoint`)

    let synced = 0
    const errors: Array<{ recordId: string; error: string }> = []

    for (const record of records) {
        try {
            console.log(`\n[SyncService] Processing record ${record.id}...`)

            // Determine sync strategy based on record state
            if (!record.checkOutTime) {
                // Case 1: Check-in only (no checkout yet)
                console.log(`[SyncService] Strategy: Check-in only (no checkout)`)
                logger.info(`[SyncService] Record ${record.id}: Check-in only`)
                await syncCheckIn(record)
                synced++

            } else if (record.recordId) {
                // Case 2: Check-out with recordId (check-in was synced)
                console.log(`[SyncService] Strategy: Check-out with existing recordId ${record.recordId}`)
                logger.info(`[SyncService] Record ${record.id}: Check-out with recordId`)
                await syncCheckOut(record)
                synced++

            } else {
                // Case 3: Both check-in and check-out offline (no recordId)
                // Need TWO sequential calls
                console.log(`[SyncService] Strategy: Both offline - two calls needed`)
                logger.info(`[SyncService] Record ${record.id}: Both offline - two calls needed`)

                // First: Sync check-in to get recordId
                await syncCheckIn(record)

                // Second: Sync check-out using the recordId we just got
                await syncCheckOut(record)

                synced++
            }

            console.log(`[SyncService] ✓ Successfully synced record ${record.id}`)
            logger.info(`[SyncService] Successfully synced record ${record.id}`)

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error'
            console.log(`[SyncService] ✗ Failed to sync record ${record.id}: ${errorMessage}`)
            logger.error(`[SyncService] Failed to sync record ${record.id}:`, error as Error)

            errors.push({
                recordId: record.id,
                error: errorMessage
            })
        }
    }

    console.log('\n' + '='.repeat(80))
    console.log('[SyncService] ===== SYNC PROCESS COMPLETED =====')
    console.log(`[SyncService] Total records: ${records.length}`)
    console.log(`[SyncService] Successfully synced: ${synced}`)
    console.log(`[SyncService] Failed: ${errors.length}`)
    if (errors.length > 0) {
        console.log('[SyncService] Errors:')
        errors.forEach((err, idx) => {
            console.log(`  ${idx + 1}. Record ${err.recordId}: ${err.error}`)
        })
    }
    console.log('='.repeat(80))

    logger.info('[SyncService] Sync completed', {
        total: records.length,
        synced,
        failed: errors.length
    })

    return {
        synced,
        failed: errors.length,
        errors
    }
}
